import React from 'react';
import * as turf from '@turf/turf';
import axios from 'axios';

import { useDispatch, useSelector } from 'react-redux';
import { SimplifyFilters } from '../../../../../redux/layer_settings/Actions';
import { cancelSearchAsync, setIsOwnershipResultsExpanded, startSearchAsync } from '../../../../../redux/ownership/Actions';
import { makeStyles, Button, Typography } from '@material-ui/core';

import ResultList from './ResultList';
import { Alert, AlertTitle } from '@material-ui/lab';

const useStyles = makeStyles((theme) => styles(theme));

function SearchActions () {
    const classes = useStyles();
    const currentProject = useSelector(state => state.project.currentProject);
    const error = useSelector(state => state.ownershipReducer.error);
    const layerId = useSelector(state => state.ownershipReducer.ownershipSearchArea.layerId);
    const areaFacet = useSelector(state => state.lookups.uiData.facetChoices.find(el => el.id === state.ownershipReducer.ownershipSearchArea.facetId));
    const areaFacetValue = useSelector(state => state.ownershipReducer.ownershipSearchArea.facetValue);
    const areaValid = useSelector(state => state.ownershipReducer.ownershipSearchArea.facetValid);
    const geojson = useSelector(state => state.ownershipReducer.ownershipSearchArea.geojson);
    const criteria = useSelector(state => state.ownershipReducer.ownershipSearchArea.criteria);
    const isSearching = useSelector(state => state.ownershipReducer.isSearching);
    const search = useSelector(state => state.ownershipReducer.search);
    const isSearchDirty = useSelector(state => state.ownershipReducer.isSearchDirty);
    const maxArea = useSelector(state => state.user.features['ownershipSearch'].maxArea);

    const layer = useSelector(state => state.layers.layers.find(l => l.id === layerId));
    if (!layer) { console.error(`Ownership layer (${layerId}) does not exist for some reason`); }

    const dispatch = useDispatch();
    function dispatchSetIsResultsExpanded () {
        dispatch(setIsOwnershipResultsExpanded(true));
    }
    function dispatchStartSearchAsync () {
        dispatch(startSearchAsync(currentProject ? currentProject.id : null, layerId, areaFacet?.id ? areaFacet.id : null, areaFacetValue, geojson ? geojson.geometry : null, criteria));
    }
    function dispatchCancelSearchAsync (id) {
        dispatch(cancelSearchAsync(id));
    }

    function getDescription (total) {
        // TODO - improve this by loading from feature
        const maxAnalysis = 10000;
        if (total > maxAnalysis) { 
            return `Too many results have been returned to run an analysis. Please refine your search by specifying a different area, or adjusting your criteria so there is less than ${maxAnalysis} results`; 
        } else if (total === 0) { 
            return 'No results where found. Please refine your search by specifying a different area, or adjusting your criteria'; 
        } else { 
            return 'You can refine your search by specifying a different area, or adjusting your criteria'; 
        }
    }

    function runSearch () {
        if (validateSearch()) { 
            dispatchStartSearchAsync(); 
        }
    }

    function cancelSearch () {
        if (search) { 
            dispatchCancelSearchAsync(search.id); 
        }
    }

    function validateSearch () {
        // TODO - Make not hardcoded field name
        // Hardcoded check that the field proprietor_name is included and has at least one value
        // Should use the isRequired prop instead
        const proprietorFilter = criteria.children.find(filter => filter.field === 'proprietor_name');
        if (!proprietorFilter || !proprietorFilter.properties.values) {
            return false;
        }
        if(!areaFacet) {
            return false
        }

        switch (areaFacet.layerType) {
            case 'all':
                return true;
            case 'drawn':
                if (!geojson) { return false; }
                if (turf.area(geojson.geometry) > maxArea) { return false; }
                return true;
            case 'fixed':
                if (!geojson) { return false; }
                if (turf.area(geojson.geometry) > maxArea) { return false; }
                return areaValid;
            default:
                console.error(`Unimplemented Area Type ${areaFacet.layerType}`);
                return false;
        }   
    }

    const isValidSearch = validateSearch();

    return (
        <>
            {
                Boolean(search && search.status === 'complete') &&
                <Alert icon={false} severity={'success'} className={classes.alertRoot}>
                    <AlertTitle classes={{ root: classes.alertTitleRoot }}>
                        Results: {search.computedData.length}
                        {
                            search.computedData.length > 0 && 
                            <Button
                                classes={{ root: classes.backButton }}
                                onClick={dispatchSetIsResultsExpanded}
                            >
                                Expand Results
                            </Button>
                        }
                    </AlertTitle>
                    {getDescription(search.computedData.length)}
                </Alert>
            }
            {
                Boolean(error) &&
                <Alert icon={false} severity="error" className={classes.alertRoot}>
                    <Typography variant="subtitle1">
                        {error.status} {error.statusText}
                    </Typography>
                    <Typography variant="body2">
                        {error.data}
                    </Typography>
                </Alert>
            }
            {/* {
                Boolean(search && ['running', 'queued', 'ready'].includes(search.status)) && 
                <Progress
                    progress={search.progress || 0}
                    status={search.status}
                    remainingTime={search.remainingTime}
                />
            } */}
            <div className={classes.actionsRoot}>
                <Button
                    variant="contained"
                    size="large"
                    color="secondary"
                    disabled={!isSearching && (!isSearchDirty || !isValidSearch)}
                    onClick={isSearching ? cancelSearch : runSearch}
                >
                    { isSearching ? 'Cancel' : ' Scan for results' }
                </Button>
                {/* <Button
                    variant="contained"
                    size="large"
                    color="secondary"
                    disabled={isAnalysing || isSearching || !isValidAnalysis}
                    onClick={runAnalysis}
                >
                    { isAnalysing ? 'Analysing' : 'Run analysis' }
                </Button> */}
            </div>
        </>
    );
}

const styles = theme => ({
    actionsRoot: {
        display: 'flex',
        '& > *': {
            flex: '1 1 0',
            '&:not(:last-child)': {
                marginRight: theme.constants.spacing.xl
            }
        }
    },
    alertRoot: {
        padding: '10px'
    },
    alertTitleRoot: {
        display: 'flex'
    },
    backButton: {
        ...theme.typography.caption,
        marginLeft: 'auto',
        color: theme.palette.primary.light,
        textTransform: 'initial',
        margin: '-6px -8px',
        justifyContent: 'end',
        minWidth: 0
    },
});

export default SearchActions;